import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ClipboardTextIcon as ClipboardText, MagnifyingGlassIcon as MagnifyingGlass, DownloadSimpleIcon as DownloadSimple } from "@phosphor-icons/react";

const AZIONE_COLOR = {
  creazione: "bg-green-100 text-green-800",
  modifica: "bg-blue-100 text-blue-800",
  eliminazione: "bg-red-100 text-red-800",
  sincronizzazione: "bg-violet-100 text-violet-800",
  elaborazione: "bg-amber-100 text-amber-800",
};

function fmtTs(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return ts; }
}

export default function Logs() {
  const [logs, setLogs] = useState(null);
  const [q, setQ] = useState("");
  const [azione, setAzione] = useState("all");

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (azione !== "all") params.azione = azione;
    api.get("/audit-logs", { params }).then(({ data }) => setLogs(data));
  }, [q, azione]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exportCsv = async () => {
    const params = {};
    if (q) params.q = q;
    if (azione !== "all") params.azione = azione;
    try {
      const res = await api.get("/audit-logs/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `log_operazioni_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export log avviato");
    } catch { toast.error("Export non riuscito"); }
  };

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12">
        <PageHeader
          title="Log operazioni"
          subtitle="Registro di audit: tutte le operazioni di scrittura con l'utente che le ha eseguite (NIS2 / ISO 27001). Conservazione: ultimi 30 giorni."
        />

        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative max-w-sm w-full">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="log-search" placeholder="Cerca per utente, risorsa o percorso..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-sm bg-white" />
          </div>
          <Select value={azione} onValueChange={setAzione}>
            <SelectTrigger className="w-48 rounded-sm bg-white" data-testid="log-azione-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le azioni</SelectItem>
              <SelectItem value="creazione">Creazione</SelectItem>
              <SelectItem value="modifica">Modifica</SelectItem>
              <SelectItem value="eliminazione">Eliminazione</SelectItem>
              <SelectItem value="sincronizzazione">Sincronizzazione</SelectItem>
              <SelectItem value="elaborazione">Elaborazione</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCsv} className="rounded-sm gap-2" data-testid="log-export-btn">
            <DownloadSimple size={16} /> Esporta CSV
          </Button>
        </div>

        <Card className="rounded-sm border-border shadow-none">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data e ora</TableHead>
                <TableHead>Utente</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead>Azione</TableHead>
                <TableHead>Risorsa</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Dettaglio</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs?.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10"><ClipboardText size={28} className="mx-auto mb-2 opacity-40" />Nessuna operazione registrata.</TableCell></TableRow>}
              {logs?.map((l) => (
                <TableRow key={l.id} data-testid="log-row" className="hover:bg-secondary">
                  <TableCell className="tabular-nums text-xs whitespace-nowrap">{fmtTs(l.timestamp)}</TableCell>
                  <TableCell className="font-medium">{l.utente}</TableCell>
                  <TableCell><Badge variant="outline" className="rounded-sm text-[10px] capitalize">{l.ruolo || "—"}</Badge></TableCell>
                  <TableCell><Badge variant="outline" className={`rounded-sm capitalize ${AZIONE_COLOR[l.azione] || ""}`}>{l.azione}</Badge></TableCell>
                  <TableCell className="font-medium">{l.risorsa}</TableCell>
                  <TableCell className="text-muted-foreground">{l.cliente_nome || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono max-w-xs truncate" title={l.path}>{l.metodo} {l.path}</TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">{l.ip || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        <p className="text-xs text-muted-foreground mt-4">Vengono registrate le operazioni di creazione, modifica, eliminazione, sincronizzazione ed elaborazione. Le consultazioni (sola lettura) non sono tracciate.</p>
      </div>
    </InternalLayout>
  );
}
